import { component } from "picoapp";

export const highlightCards = component((node: HTMLElement) => {
  let activeCard: Element | null = null;

  const highlightCardElements =
    node.querySelectorAll<HTMLElement>(".highlight-card");

  const imageContainerTransformations = [
    "shadow-highlight-card",
    "!scale-x-[1.073]",
    "!scale-y-[1.14]",
    "md:!scale-x-[1.055]",
    "md:!scale-y-[1.073]",
    "mg:!scale-x-[1.15]",
    "mg:!scale-y-[1.045]",
    "!z-10",
    "!duration-800",
  ];

  const imageTransformations = [
    "!scale-x-[calc(1/1.073)]",
    "!scale-y-[calc(1/1.14)]",
    "md:!scale-x-[calc(1/1.055)]",
    "md:!scale-y-[calc(1/1.073)]",
    "mg:!scale-x-[calc(1/1.15)]",
    "mg:!scale-y-[calc(1/1.045)]",
    "!duration-800",
  ];

  const iconTransformations = (icon: HTMLElement) => [
    "rotate-45",
    icon.dataset.bodyClass ?? "",
    icon.dataset.activeBorderClass ?? "",
    "!-bottom-3",
    "!right-1",
    "md:!bottom-0.5",
    "mg:!bottom-[11px]",
    "mg:!-right-2",
    "!duration-800",
  ];

  const resetCards = () => {
    const allHighlightCards =
      node.querySelectorAll<HTMLElement>(".highlight-card");
    const allBackgroundElements =
      node.querySelectorAll<HTMLElement>(".card-background");
    const allBodyElements = node.querySelectorAll<HTMLElement>(".card-body");
    const allImageContainers = node.querySelectorAll<HTMLElement>(
      ".card-image-container"
    );
    const allImages = node.querySelectorAll<HTMLElement>(".card-image");
    const allIcons = node.querySelectorAll<HTMLElement>(".card-icon");
    const allTitles = node.querySelectorAll<HTMLElement>(".card-title");

    allHighlightCards?.forEach((card) =>
      card.setAttribute("aria-expanded", "false")
    );

    allBackgroundElements?.forEach((element) =>
      element?.classList.add("opacity-0", "translate-y-full")
    );

    allBodyElements?.forEach((element) =>
      element?.classList.add("opacity-0", "translate-y-full")
    );

    allImageContainers?.forEach((container) =>
      container?.classList.remove(...imageContainerTransformations)
    );

    allImages?.forEach((image) => {
      image?.classList.remove(...imageTransformations);
    });

    allIcons?.forEach((icon) => {
      icon?.classList.remove(...iconTransformations(icon));
      icon?.classList.add(
        ...[
          icon.dataset.titleClass || "",
          icon.dataset.inactiveBorderClass || "",
        ]
      );
    });

    allTitles?.forEach((title) => {
      title?.classList.remove("opacity-0");
    });
  };

  const handleHighlightCardClick = (
    e: Event | (Event & { currentTarget: EventTarget })
  ) => {
    if (!(e.currentTarget instanceof Element)) {
      return;
    }

    const backgroundElement =
      e.currentTarget.querySelector<HTMLElement>(".card-background");
    const bodyElement =
      e.currentTarget.querySelector<HTMLElement>(".card-body");
    const imageContainer = e.currentTarget.querySelector<HTMLElement>(
      ".card-image-container"
    );
    const image = e.currentTarget.querySelector<HTMLElement>(".card-image");
    const icon = e.currentTarget.querySelector<HTMLElement>(".card-icon");
    const title = e.currentTarget.querySelector<HTMLElement>(".card-title");

    if (e.currentTarget === activeCard) {
      backgroundElement?.classList.add("opacity-0", "translate-y-full");
      bodyElement?.classList.add("opacity-0", "translate-y-full");
      imageContainer?.classList.remove(...imageContainerTransformations);
      image?.classList.remove(...imageTransformations);
      icon?.classList.remove(...iconTransformations(icon));
      icon?.classList.add(
        ...[
          icon.dataset.titleClass ?? "",
          icon.dataset.inactiveBorderClass ?? "",
        ]
      );
      title?.classList.remove("opacity-0");
      activeCard = null;
      e.currentTarget.setAttribute("aria-expanded", "false");
    } else {
      resetCards();
      backgroundElement?.classList.remove("opacity-0", "translate-y-full");
      imageContainer?.classList.add(...imageContainerTransformations);
      image?.classList.add(...imageTransformations);
      bodyElement?.classList.remove("opacity-0", "translate-y-full");
      icon?.classList.remove(
        ...[
          icon.dataset.titleClass ?? "",
          icon.dataset.inactiveBorderClass ?? "",
        ]
      );
      icon?.classList.add(...iconTransformations(icon));
      title?.classList.add("opacity-0");
      activeCard = e.currentTarget;
      e.currentTarget.setAttribute("aria-expanded", "true");
    }
  };

  const handleHighlightCardKeypress = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleHighlightCardClick(e);
    }
  };

  highlightCardElements?.forEach((card) => {
    card?.addEventListener("click", handleHighlightCardClick);
    card?.addEventListener("keypress", handleHighlightCardKeypress);
  });

  return () => {
    highlightCardElements?.forEach((card) => {
      card?.removeEventListener("click", handleHighlightCardClick);
      card?.removeEventListener("keypress", handleHighlightCardKeypress);
    });
  };
});
